import {
    ILocationPermissionsResponse,
    ILocationPermissionsUpdateRequest,
    LocationPermissionsUpdateRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { SwitchInlineEditor } from 'components/inline_edit/ToggleInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';

type Perms = ILocationPermissionsUpdateRequest;
type UserGroupPerms = {
    // exclude non-bools (invalid for this component)
    [K in keyof Perms as Perms[K] extends boolean ? K : never]?: Perms[K];
};
type PermsKey = keyof UserGroupPerms;

export type LocationPermissionsCellProps = {
    locationId: string;
    data: ILocationPermissionsResponse;
    permsKey: PermsKey;
    onChange: () => void;
};

export function LocationPermissionsCell({
    locationId,
    data,
    permsKey,
    onChange,
}: LocationPermissionsCellProps) {
    const { canEditLocationFeaturePermissions } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updatePermission, isPending } = useMutation({
        meta: { name: 'update-location-permissions' },
        mutationFn: async (value: boolean) => {
            await sdClient.updateLocationPermissions({
                id: locationId,
                body: new LocationPermissionsUpdateRequest({
                    ...data,
                    DeskChatAutoReplyEnabledFor: data.DeskChatAutoReplyEnabledFor,
                    [permsKey]: value,
                }),
            });
        },

        onSuccess: () => {
            onChange();
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location permissions',
            }),
    });

    return (
        <SwitchInlineEditor
            value={data[permsKey]}
            onConfirm={(v) => {
                if (v !== undefined) {
                    updatePermission(v);
                }
            }}
            isDisabled={!canEditLocationFeaturePermissions || isPending}
            renderValue={(val) => (val ? 'Enabled' : 'Disabled')}
        />
    );
}
